// client/src/pages/seo/MenuWithPicturesEn.jsx
import React, { useEffect } from "react";
import SeoRelatedLinks from "../../components/seo/SeoRelatedLinks.jsx";
import SeoHeader from "../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../components/seo/SeoWorkSteps.jsx";

const sections = [
  {
    id: "start",
    label: "Pictures in a menu should guide the guest, not just decorate the page",
  },
  {
    id: "why",
    label: "Why can pictures be important in a digital menu?",
  },
  {
    id: "where",
    label: "Which dishes should have pictures first?",
  },
  {
    id: "quality",
    label: "What photo quality does a good menu need?",
  },
  {
    id: "workflow",
    label: "How do you manage pictures in a digital menu?",
  },
  {
    id: "mistakes",
    label: "What mistakes happen with menus that use pictures?",
  },
  {
    id: "recommendation",
    label: "How would I start as a restaurant owner?",
  },
  {
    id: "next-steps",
    label: "What should you do now?",
  },
  {
    id: "bestplace",
    label: "How Best Place helps you create a menu with pictures",
  },
  {
    id: "faq",
    label: "FAQ about menus with pictures",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visual idea">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>
      <div>
        <strong>Idea for illustration:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

export default function MenuWithPicturesEn() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "en");
    document.title =
      "Menu with Pictures: Present Restaurant Dishes Better Online | Best Place";
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="en"
        langLinks={{
          en: "/en/menu-with-pictures",
          de: "/de/speisekarte-mit-bildern",
          no: "/no/meny-med-bilder",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Menu with pictures: present restaurant dishes better online
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Pictures can make a restaurant menu stronger. A good photo helps
                guests understand a dish faster, makes recommendations more
                visible and can be especially useful for new, seasonal or
                unfamiliar items.
              </p>

              <p className="seoHero__text">
                But pictures are not automatically helpful. Poor photos,
                inconsistent style or too many images can overload a digital
                menu. The important question is where pictures actually support
                the guest’s decision.
              </p>
            </div>
          </div>
        </div>
      </section>

      <SeoContactSection lang="en" apiOrigin={CALLBACK_API_ORIGIN} />

      <section className="articls">
        <h2>Content of the article</h2>

        <nav className="qrArticle__nav" aria-label="Article navigation">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>
            Pictures in a menu should guide the guest, not just decorate the
            page
          </h2>

          <p>
            Many restaurants start with the idea that every dish needs a photo.
            That sounds logical, but it is not always the best approach. On a
            long menu, too many images can feel noisy and slow down the mobile
            experience.
          </p>

          <p>
            A better approach is selective: use pictures where they help guests
            decide. For example, for recommendations, new dishes, signature
            items, desserts, cocktails or seasonal offers.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Weaker</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Every dish gets any available photo.
                </li>
                <li className="text__seo-li">
                  Different lighting styles.
                </li>
                <li className="text__seo-li">
                  Dark or blurry images.
                </li>
                <li className="text__seo-li">
                  Menu becomes slow and crowded.
                </li>
                <li className="text__seo-li">
                  Photos do not match the real dish.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Better</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Use images for important dishes first.
                </li>
                <li className="text__seo-li">
                  Keep one visual style.
                </li>
                <li className="text__seo-li">
                  Use bright, clear photos.
                </li>
                <li className="text__seo-li">
                  Respect mobile loading speed.
                </li>
                <li className="text__seo-li">
                  Show realistically what the guest receives.
                </li>
              </ul>
            </div>
          </div>

          <h3>Test pictures with a few dishes first</h3>

          <p>
            Start with recommendations, daily specials or bestsellers. This
            makes it easier to see whether pictures improve your digital menu
            without overloading the page.
          </p>
        </section>

        <section id="why" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Why can pictures be important in a digital menu?</h2>

            <p>
              Guests do not decide only by reading. Names, prices and
              descriptions matter, but pictures can communicate faster: portion
              size, style, ingredients, presentation and mood.
            </p>

            <p>
              Pictures are especially useful when a dish is hard to imagine,
              when guests are not familiar with the cuisine or when the
              restaurant wants to highlight specific items.
            </p>

            <h3>Pictures can make the decision easier</h3>

            <p>
              A menu with pictures is not only a design decision. It can change
              how quickly guests understand dishes and which offers they notice
              first.
            </p>

            <p>
              The strongest photos do not simply decorate the menu. They answer
              a real guest question: what does this dish look like, how is it
              served and does it match what I want right now?
            </p>
          </div>
        </section>

        <section id="where" className="acquaintance">
          <h2>Which dishes should have pictures first?</h2>

          <p>
            Not every dish needs a picture. In many cases, less is better. The
            strongest images are the ones that solve a specific problem for the
            guest.
          </p>

          <ul className="qrArticle__block-li">
            <li className="text__seo-li">House recommendations.</li>
            <li className="text__seo-li">Daily specials.</li>
            <li className="text__seo-li">Seasonal offers.</li>
            <li className="text__seo-li">Desserts.</li>
            <li className="text__seo-li">Cocktails and drinks.</li>
            <li className="text__seo-li">Unfamiliar specialties.</li>
            <li className="text__seo-li">Dishes for tourists.</li>
            <li className="text__seo-li">Higher-priced dishes.</li>
          </ul>

          <p>
            A picture is especially useful when it answers a question: What does
            the dish look like? How large is the portion? Is it light, rich,
            modern or traditional?
          </p>

          <VisualIdea>
            A digital menu with three highlighted items: daily recommendation,
            dessert and cocktail. All photos have the same bright style.
          </VisualIdea>
        </section>

        <section id="quality" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What photo quality does a good menu need?</h2>

            <p>
              Good menu photos do not have to look like a large advertising
              campaign. But they should be clean, bright, realistic and
              consistent.
            </p>

            <p>
              Guests should be able to expect something close to what they see
              in the photo. A picture that looks much better than the real dish
              may create attention in the short term, but it can damage trust
              later.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Natural light.</li>
              <li className="text__seo-li">Calm background.</li>
              <li className="text__seo-li">Real dish.</li>
              <li className="text__seo-li">Consistent image style.</li>
              <li className="text__seo-li">Not too many filters.</li>
              <li className="text__seo-li">Clear sharpness.</li>
              <li className="text__seo-li">Mobile file size.</li>
              <li className="text__seo-li">Realistic portion.</li>
            </ul>

            <h3>Photos are part of the restaurant’s digital presentation</h3>

            <p>
              In a digital menu, photos become part of how the restaurant is
              perceived. They should support trust, not create unrealistic
              expectations.
            </p>
          </div>
        </section>

        <section id="workflow" className="acquaintance">
          <h2>How do you manage pictures in a digital menu?</h2>

          <p>
            The best workflow is simple: do not start with 80 photos. Start with
            a small selection and check whether the images actually help inside
            the menu.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Select dishes:</strong> Start with bestsellers,
              recommendations or seasonal offers.
            </li>

            <li className="text__seo-li">
              <strong>Choose one visual style:</strong> Similar angle, similar
              lighting and a calm background.
            </li>

            <li className="text__seo-li">
              <strong>Upload photos:</strong> Use pictures only where they help
              the guest.
            </li>

            <li className="text__seo-li">
              <strong>Check on smartphone:</strong> Make sure the menu loads
              fast and stays easy to scan.
            </li>

            <li className="text__seo-li">
              <strong>Replace regularly:</strong> Seasonal dishes and daily
              offers need current photos.
            </li>
          </ol>

          <p>
            A digital menu is more flexible than a printed one. Pictures can be
            added, replaced or removed without reprinting the QR code.
          </p>
        </section>

        <section id="mistakes" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What mistakes happen with menus that use pictures?</h2>

            <p>
              Pictures can help, but they can also hurt. That usually happens
              when they are added without a clear purpose.
            </p>

            <div className="seoComparison">
              <div className="seoComparison__col">
                <h3>Common mistakes</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Too many pictures at once.
                  </li>
                  <li className="text__seo-li">
                    Dark or blurry photos.
                  </li>
                  <li className="text__seo-li">
                    Inconsistent image styles.
                  </li>
                  <li className="text__seo-li">
                    Over-edited images that feel unrealistic.
                  </li>
                  <li className="text__seo-li">
                    Large files that slow down the menu.
                  </li>
                  <li className="text__seo-li">
                    Photos do not match the real dish.
                  </li>
                  <li className="text__seo-li">
                    Old images stay online after the dish changes.
                  </li>
                </ul>
              </div>

              <div className="seoComparison__col seoComparison__col--highlight">
                <h3>Better</h3>

                <ul className="qrArticle__block-li">
                  <li className="text__seo-li">
                    Start with a few important images.
                  </li>
                  <li className="text__seo-li">
                    Use bright and clear photos.
                  </li>
                  <li className="text__seo-li">
                    Keep a consistent style.
                  </li>
                  <li className="text__seo-li">
                    Show dishes realistically.
                  </li>
                  <li className="text__seo-li">
                    Optimize images for mobile use.
                  </li>
                  <li className="text__seo-li">
                    Review photos regularly.
                  </li>
                  <li className="text__seo-li">
                    Remove old images after menu changes.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="recommendation" className="acquaintance">
          <h2>How would I start as a restaurant owner?</h2>

          <p>
            I would not try to photograph every dish immediately. That takes too
            much time and often creates inconsistent results.
          </p>

          <ol className="seoSteps">
            <li className="text__seo-li">
              <strong>Start with 5 to 10 dishes:</strong> Recommendations,
              bestsellers or seasonal dishes.
            </li>

            <li className="text__seo-li">
              <strong>Choose one photo style:</strong> Same place, similar light,
              similar angle.
            </li>

            <li className="text__seo-li">
              <strong>Test on smartphone:</strong> Check whether the pictures
              look good and the menu does not feel overloaded.
            </li>

            <li className="text__seo-li">
              <strong>Observe for one week:</strong> Do guests ask about those
              dishes? Are they easier to explain?
            </li>

            <li className="text__seo-li">
              <strong>Expand later:</strong> Add more pictures only where they
              really help.
            </li>
          </ol>

          <p>
            The goal is not to build a photo archive. The goal is to make the
            guest’s decision easier.
          </p>
        </section>

        <section id="next-steps" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>What should you do now?</h2>

            <p>
              If you want to create a menu with pictures, start with a small
              clean test.
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">Create a digital menu.</li>
              <li className="text__seo-li">
                Select 5 to 10 important dishes.
              </li>
              <li className="text__seo-li">
                Take bright, realistic photos.
              </li>
              <li className="text__seo-li">
                Upload the images to the menu.
              </li>
              <li className="text__seo-li">
                Open the menu on a smartphone.
              </li>
              <li className="text__seo-li">
                Check loading speed and readability.
              </li>
              <li className="text__seo-li">
                Add more pictures only after the first test.
              </li>
            </ul>

            <VisualIdea>
              A small photo test: five selected dishes, one consistent photo
              style and a smartphone view that shows whether the menu remains
              clear and fast.
            </VisualIdea>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="en" />
        </div>

        <section id="faq" className="acquaintance">
          <h2>FAQ about menus with pictures</h2>

          <div className="seoFaq">
            <details>
              <summary>Should every dish have a picture?</summary>
              <p>
                Not necessarily. It is often better to use pictures selectively
                for recommendations, bestsellers, daily specials or dishes that
                are hard to imagine.
              </p>
            </details>

            <details>
              <summary>Can I take menu photos with a smartphone?</summary>
              <p>
                Yes, if lighting, sharpness and style are good. The photo should
                realistically show what the guest can expect.
              </p>
            </details>

            <details>
              <summary>Can pictures make the menu slower?</summary>
              <p>
                Large and unoptimized images can slow down a digital menu.
                Images should be selected carefully and optimized for mobile
                use.
              </p>
            </details>

            <details>
              <summary>Are pictures better than descriptions?</summary>
              <p>
                No. Good pictures and short descriptions work together. The
                image creates the first impression, while the description
                explains ingredients, style or details.
              </p>
            </details>

            <details>
              <summary>Which dishes should I photograph first?</summary>
              <p>
                Start with bestsellers, house recommendations, seasonal dishes,
                desserts or dishes that guests may not know well.
              </p>
            </details>

            <details>
              <summary>Can I replace pictures later?</summary>
              <p>
                Yes. In a digital menu, photos can be replaced, removed or added
                without reprinting the QR code.
              </p>
            </details>
          </div>
        </section>
        <SeoRelatedLinks
          lang="en"
          group="menu"
          exclude="/en/menu-with-pictures"
        />
      </article>
    </main>
  );
}